import { styled } from "@mui/material/styles";
import type { Duration } from "dayjs/plugin/duration";
import groupBy from "lodash/groupBy";

import { ProductStartTimeListNode } from "../../apiHooks/useProduct";
import { Typography } from "../../components";
import { LabelValue } from "../../components/LabelValue";
import { useLanguage } from "../../hooks/useLanguage";
import { useProductTranslation } from "../../hooks/useProductTranslation";
import { durationFromString, formatDurationToTextAlternative } from "../../utils/formatters/formatDuration";

const EntriesWrapper = styled("div")(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(1, 4),
    gridTemplateColumns: "max-content auto",
}));

function LabelWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Typography color="dark" size="large">
            {children}
        </Typography>
    );
}

function ValueWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Typography color="subtle" size="large">
            {children}
        </Typography>
    );
}

function Entry({ duration, startTimes }: { duration: Duration; startTimes: Array<string | null> }) {
    const [t] = useProductTranslation();
    const durationLabel = t("label.duration");
    const startTimeLabel = t("label.startTime");
    const startTimesLabel = t("label.startTimes");
    return (
        <>
            <LabelValue label={<LabelWrapper>{durationLabel}</LabelWrapper>}>
                <ValueWrapper>{formatDurationToTextAlternative(duration)}</ValueWrapper>
            </LabelValue>
            <LabelValue label={<LabelWrapper>{startTimes.length > 1 ? startTimesLabel : startTimeLabel}</LabelWrapper>}>
                <ValueWrapper>{startTimes.join(", ")}</ValueWrapper>
            </LabelValue>
        </>
    );
}

export type ProductSectionStartTimesProps = {
    startTimes: ProductStartTimeListNode[];
};

export const ProductSectionStartTimeEntries = ({ startTimes }: ProductSectionStartTimesProps) => {
    const groupedByDuration = groupBy(startTimes, "duration");
    const language = useLanguage();

    return (
        <EntriesWrapper>
            {Object.entries(groupedByDuration).map(([duration, startTimes], idx) => {
                const finalDuration = durationFromString(language, duration);
                if (!finalDuration) {
                    return null;
                }
                return <Entry key={idx} duration={finalDuration} startTimes={startTimes.map((x) => x.startTime)} />;
            })}
        </EntriesWrapper>
    );
};
